import { Row, Col } from 'antd';
import { useFormikContext } from 'formik';
import { type ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { type FinanceProductFormValues } from '..';
import { type GetLocalVariantsOptionsQueryVariables } from '../../../../../api/queries/getLocalVariantsOptions';
import SelectField from '../../../../../components/fields/SelectField';
import { type TransferFieldProps } from '../../../../../components/fields/TransferField';
import CollapsibleWrapper, { Panel } from '../../../../../components/wrappers/CollapsibleWrapper';
import YesNoField from '../ParametersForm/YesNoField';
import StyledTransferField from './StyledTransferField';
import useFinanceProductEngineTypeOptions from './useFinanceProductEngineTypeOptions';
import useLocalVehicles, { hasVehicle } from './useVehicles';

const colSpan = { lg: 8, xs: 24 };

type RecordType = {
    key: string;
    title: string;
};

type VehicleAssignmentsSectionProps = {
    header: ReactNode;
    name: string;
    filter?: GetLocalVariantsOptionsQueryVariables['filter'];
    transferFieldProps?: Omit<TransferFieldProps<RecordType>, 'name' | 'dataSource'>;
    disabled?: boolean;
};

const filterOption = (inputValue: string, option: RecordType) =>
    option.title.toLowerCase().indexOf(inputValue.toLowerCase()) > -1;

const VehicleAssignmentsSection = ({
    header,
    name,
    filter,
    transferFieldProps,
    disabled = false,
}: VehicleAssignmentsSectionProps) => {
    const { t } = useTranslation(['financeProductDetails']);

    const vehicleDataSource = useLocalVehicles(filter);

    const engineTypeOptions = useFinanceProductEngineTypeOptions();

    const { values, setFieldValue } = useFormikContext<FinanceProductFormValues>();
    const { isAddOnSpecificVehicles } = values?.vehicleReferenceParameters?.variant || {};
    useEffect(() => {
        if (!isAddOnSpecificVehicles) {
            const { variantSuiteIds = [] } = values;
            setFieldValue(
                'variantSuiteIds',
                variantSuiteIds.filter(id => !hasVehicle(vehicleDataSource, id))
            );
        }
    }, [isAddOnSpecificVehicles]);

    return (
        <CollapsibleWrapper defaultActiveKey={name}>
            <Panel key={name} header={header}>
                <Row gutter={14}>
                    <Col {...colSpan}>
                        <SelectField
                            {...t('financeProductDetails:fields.matchEngineType', { returnObjects: true })}
                            disabled={disabled}
                            mode="multiple"
                            name="vehicleReferenceParameters.variant.engineType"
                            options={engineTypeOptions}
                            showSearch
                        />
                    </Col>
                    <Col {...colSpan}>
                        <YesNoField
                            {...t('financeProductDetails:fields.isAddOnSpecificVehicles', { returnObjects: true })}
                            disabled={disabled}
                            name="vehicleReferenceParameters.variant.isAddOnSpecificVehicles"
                        />
                    </Col>
                </Row>
                {isAddOnSpecificVehicles && (
                    <Row gutter={14}>
                        <Col lg={16} xs={24}>
                            <StyledTransferField
                                dataSource={vehicleDataSource}
                                disabled={disabled}
                                filterOption={filterOption}
                                name={name}
                                render={(item: RecordType) => item.title}
                                {...transferFieldProps}
                            />
                        </Col>
                    </Row>
                )}
            </Panel>
        </CollapsibleWrapper>
    );
};

export default VehicleAssignmentsSection;
